import type { UserPortfolioSnapshotWire } from './types.js';

export interface UserPortfolioHeldSubjects {
  readonly rateSubjects: readonly string[];
  readonly lifecycleSubjects: readonly string[];
  readonly allSubjects: readonly string[];
  readonly positivePositionCount: number;
}

export type UserPortfolioWalletEventKind =
  | 'activity'
  | 'backing'
  | 'boost'
  | 'claim'
  | 'quote_balance'
  | 'trade';

export interface UserPortfolioWalletEventIdentity {
  readonly actorWallet: string;
  readonly kind: UserPortfolioWalletEventKind;
}

export function userPortfolioRepresentedWallets(
  knownWallets: readonly string[],
  snapshotWallets: readonly string[],
): readonly string[] {
  return [...new Set([...knownWallets, ...snapshotWallets].filter(isSubjectToken))].sort();
}

export function userPortfolioWalletSubjects(wallets: readonly string[]): readonly string[] {
  const subjects = new Set<string>();
  for (const wallet of wallets) {
    if (!isSubjectToken(wallet)) continue;
    subjects.add(`morfi.market.*.user.${wallet}.trade.v0`);
    subjects.add(`morfi.amm.*.user.${wallet}.activity.v0`);
    subjects.add(`morfi.market.*.user.${wallet}.backing.v0`);
    subjects.add(`morfi.market.*.user.${wallet}.boost.v0`);
    subjects.add(`morfi.market.*.user.${wallet}.claim.v0`);
    subjects.add(`morfi.wallet.${wallet}.quote_balance.v0`);
  }
  return [...subjects].sort();
}

export function userPortfolioHeldSubjects(
  snapshot: UserPortfolioSnapshotWire,
): UserPortfolioHeldSubjects {
  const rateSubjects = new Set<string>();
  const lifecycleSubjects = new Set<string>();
  let positivePositionCount = 0;

  for (const position of snapshot.positions) {
    if (!isPositiveWire(position.balanceD6)) continue;
    positivePositionCount += 1;
    if (!isSubjectToken(position.market)) continue;
    lifecycleSubjects.add(`morfi.market.${position.market}.lifecycle.v0`);
    lifecycleSubjects.add(`morfi.market.${position.market}.resolution.v0`);

    const entry = snapshot.poolSnapshotsByMarket[position.market];
    if (entry?.status !== 'complete') continue;
    if (entry.snapshot.lifecycle === 'trading') {
      rateSubjects.add(`morfi.market.${position.market}.user.*.trade.v0`);
      continue;
    }
    const outcome = entry.snapshot.outcomes.find(
      (candidate) =>
        candidate.outcomeIndex === position.outcomeIndex &&
        candidate.outcomeMint === position.outcomeMint,
    );
    if (
      entry.snapshot.lifecycle === 'graduated' &&
      outcome?.cpSwap !== null &&
      outcome?.cpSwap !== undefined &&
      isSubjectToken(position.outcomeMint)
    ) {
      rateSubjects.add(`morfi.amm.${position.outcomeMint}.user.*.activity.v0`);
    }
  }

  const rate = [...rateSubjects].sort();
  const lifecycle = [...lifecycleSubjects].sort();
  return {
    rateSubjects: rate,
    lifecycleSubjects: lifecycle,
    allSubjects: [...rate, ...lifecycle].sort(),
    positivePositionCount,
  };
}

export function userPortfolioWalletEventIdentity(
  subject: string,
): UserPortfolioWalletEventIdentity | null {
  const tokens = subject.split('.');
  if (
    tokens.length === 5 &&
    tokens[0] === 'morfi' &&
    tokens[1] === 'wallet' &&
    tokens[3] === 'quote_balance' &&
    tokens[4] === 'v0' &&
    isSubjectToken(tokens[2] ?? '')
  ) {
    return { actorWallet: tokens[2] ?? '', kind: 'quote_balance' };
  }
  if (
    tokens.length !== 7 ||
    tokens[0] !== 'morfi' ||
    tokens[3] !== 'user' ||
    tokens[6] !== 'v0' ||
    !isSubjectToken(tokens[2] ?? '') ||
    !isSubjectToken(tokens[4] ?? '')
  ) {
    return null;
  }
  const channel = tokens[1];
  const event = tokens[5];
  if (channel === 'market' && event === 'trade') {
    return { actorWallet: tokens[4] ?? '', kind: 'trade' };
  }
  if (channel === 'market' && event === 'backing') {
    return { actorWallet: tokens[4] ?? '', kind: 'backing' };
  }
  if (channel === 'market' && event === 'boost') {
    return { actorWallet: tokens[4] ?? '', kind: 'boost' };
  }
  if (channel === 'amm' && event === 'activity') {
    return { actorWallet: tokens[4] ?? '', kind: 'activity' };
  }
  if (channel === 'market' && event === 'claim') {
    return { actorWallet: tokens[4] ?? '', kind: 'claim' };
  }
  return null;
}

function isPositiveWire(value: string): boolean {
  return /^(?:0|[1-9]\d*)$/.test(value) && BigInt(value) > BigInt(0);
}

function isSubjectToken(value: string): boolean {
  return value.length > 0 && !/[.>*\s]/.test(value);
}
